import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGoogleLogin } from '@react-oauth/google';
import { GraduationCap, AlertCircle, Sun, Moon, X } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import Toast from '../../components/ui/Toast';
import { authService } from '../../services/api';

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID;

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 48 48" style={{ flexShrink: 0 }}>
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
    <path fill="none" d="M0 0h48v48H0z"/>
  </svg>
);

// Isolated component so useGoogleLogin only mounts when a client ID is present.
const GoogleSignInButton = ({ onSuccess, onError, loading, dark }) => {
  const signIn = useGoogleLogin({ onSuccess, onError });
  return (
    <button
      onClick={() => signIn()}
      disabled={loading}
      style={{
        width: '100%', padding: '11px 16px', borderRadius: 10,
        background: dark ? '#1E293B' : '#fff',
        border: `1px solid ${dark ? '#334155' : '#D1D5DB'}`,
        color: dark ? '#F1F5F9' : '#374151',
        fontSize: 14, fontWeight: 600,
        cursor: loading ? 'not-allowed' : 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
        opacity: loading ? 0.7 : 1,
      }}
    >
      <GoogleIcon />
      {loading ? 'Signing in...' : 'Sign in with Google'}
    </button>
  );
};

const ForgotPasswordModal = ({ dark, onClose }) => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) { setError('Please enter your email address.'); return; }
    setLoading(true);
    setError('');
    try {
      await authService.forgotPassword(email.trim());
      setSent(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const overlay = {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1000, padding: 16,
  };
  const card = {
    width: '100%', maxWidth: 400,
    background: dark ? '#1E293B' : '#fff',
    borderRadius: 16, padding: 32,
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
    position: 'relative',
  };

  return (
    <div style={overlay} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={card}>
        <button
          onClick={onClose}
          style={{ position: 'absolute', top: 16, right: 16, background: 'none', border: 'none', cursor: 'pointer', color: dark ? '#64748B' : '#94A3B8' }}
        >
          <X size={18} />
        </button>
        <h2 style={{ fontSize: 18, fontWeight: 700, color: dark ? '#F1F5F9' : '#0F172A', margin: '0 0 6px' }}>
          Forgot Password?
        </h2>
        <p style={{ fontSize: 13, color: dark ? '#64748B' : '#94A3B8', margin: '0 0 20px', lineHeight: 1.5 }}>
          Enter your registered email address and we'll send you a link to reset your password.
        </p>

        {sent ? (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>📧</div>
            <p style={{ fontSize: 14, color: dark ? '#94A3B8' : '#475569', lineHeight: 1.6 }}>
              If that email is registered, a reset link has been sent. Check your inbox (and spam folder).
            </p>
            <button
              onClick={onClose}
              style={{
                marginTop: 20, padding: '10px 28px', borderRadius: 8,
                background: 'linear-gradient(135deg,#3B82F6,#8B5CF6)',
                color: '#fff', border: 'none', fontWeight: 600, fontSize: 14, cursor: 'pointer',
              }}
            >
              Back to Sign In
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            {error && (
              <div style={{ padding: '8px 12px', borderRadius: 8, background: '#FEF2F2', color: '#EF4444', fontSize: 13, marginBottom: 14, display: 'flex', alignItems: 'center', gap: 6 }}>
                <AlertCircle size={13} />{error}
              </div>
            )}
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: dark ? '#94A3B8' : '#64748B', marginBottom: 5 }}>
              Email Address
            </label>
            <input
              type="email" value={email} onChange={e => setEmail(e.target.value)}
              placeholder="your@email.com" autoFocus
              style={{
                width: '100%', padding: '9px 12px', borderRadius: 8, boxSizing: 'border-box',
                border: `1px solid ${dark ? '#475569' : '#D1D5DB'}`,
                background: dark ? '#0F172A' : '#F8FAFC',
                color: dark ? '#F1F5F9' : '#0F172A', fontSize: 14, outline: 'none', marginBottom: 18,
              }}
            />
            <button
              type="submit" disabled={loading}
              style={{
                width: '100%', padding: '11px', borderRadius: 8,
                background: loading ? '#94A3B8' : 'linear-gradient(135deg,#3B82F6,#8B5CF6)',
                color: '#fff', border: 'none', fontWeight: 600, fontSize: 14,
                cursor: loading ? 'not-allowed' : 'pointer',
              }}
            >
              {loading ? 'Sending...' : 'Send Reset Link'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

const LoginPage = () => {
  const { login, googleLogin } = useAuth();
  const { dark, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const [form, setForm] = useState({ email: 'admin@crm.com', password: 'Admin@123' });
  const [error, setError] = useState('');
  const [toast, setToast] = useState(null);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);

  const handleLogin = async () => {
    try {
      setError('');
      const user = await login(form);
      setToast({ msg: `Welcome back, ${user.firstName}!`, type: 'success' });
      setTimeout(() => navigate('/dashboard'), 500);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Invalid credentials');
    }
  };

  const handleGoogleSuccess = async (tokenResponse) => {
    setGoogleLoading(true);
    setError('');
    try {
      const user = await googleLogin(tokenResponse.access_token);
      setToast({ msg: `Welcome back, ${user.firstName}!`, type: 'success' });
      setTimeout(() => navigate('/dashboard'), 500);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Google sign-in failed');
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleGoogleError = () =>
    setError('Google sign-in was cancelled or failed. Please try again.');


  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: dark ? '#0F172A' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      fontFamily: '-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif',
    }}>
      {forgotOpen && <ForgotPasswordModal dark={dark} onClose={() => setForgotOpen(false)} />}
      {toast && <Toast message={toast.msg} type={toast.type} onClose={() => setToast(null)} />}

      <div style={{
        width: 420, background: dark ? '#1E293B' : '#fff',
        borderRadius: 20, padding: 40, boxShadow: '0 25px 60px rgba(0,0,0,0.3)',
      }}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{
            width: 56, height: 56, borderRadius: 14,
            background: 'linear-gradient(135deg,#3B82F6,#8B5CF6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 16px',
          }}>
            <GraduationCap size={28} color="#fff" />
          </div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: dark ? '#F1F5F9' : '#0F172A', margin: '0 0 4px' }}>
            Course Lead CRM
          </h1>
          <p style={{ fontSize: 13, color: dark ? '#64748B' : '#94A3B8', margin: 0 }}>
            Enterprise Lead Management Portal
          </p>
        </div>

        {error && (
          <div style={{
            padding: '8px 14px', borderRadius: 8, background: '#FEF2F2',
            color: '#EF4444', fontSize: 13, marginBottom: 16,
            display: 'flex', alignItems: 'center', gap: 6,
          }}>
            <AlertCircle size={14} />{error}
          </div>
        )}

        <div style={{ marginBottom: 14 }}>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: dark ? '#94A3B8' : '#64748B', marginBottom: 5 }}>
            Email Address
          </label>
          <input
            type="email" value={form.email}
            onChange={e => setForm({ ...form, email: e.target.value })}
            placeholder="Enter your email"
            onKeyDown={e => e.key === 'Enter' && handleLogin()}
            style={{
              width: '100%', padding: '9px 12px', borderRadius: 8,
              border: `1px solid ${dark ? '#475569' : '#D1D5DB'}`,
              background: dark ? '#0F172A' : '#F8FAFC',
              color: dark ? '#F1F5F9' : '#0F172A', fontSize: 14, outline: 'none', boxSizing: 'border-box',
            }}
          />
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: dark ? '#94A3B8' : '#64748B', marginBottom: 5 }}>
            Password
          </label>
          <input
            type="password" value={form.password}
            onChange={e => setForm({ ...form, password: e.target.value })}
            placeholder="Enter password"
            onKeyDown={e => e.key === 'Enter' && handleLogin()}
            style={{
              width: '100%', padding: '9px 12px', borderRadius: 8,
              border: `1px solid ${dark ? '#475569' : '#D1D5DB'}`,
              background: dark ? '#0F172A' : '#F8FAFC',
              color: dark ? '#F1F5F9' : '#0F172A', fontSize: 14, outline: 'none', boxSizing: 'border-box',
            }}
          />
        </div>

        <div style={{ textAlign: 'right', marginBottom: 8, marginTop: 2 }}>
          <button
            onClick={() => setForgotOpen(true)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: '#3B82F6', fontWeight: 500, padding: 0 }}
          >
            Forgot password?
          </button>
        </div>

        <button onClick={handleLogin} style={{
          width: '100%', padding: '12px', borderRadius: 10,
          background: 'linear-gradient(135deg,#3B82F6,#8B5CF6)',
          color: '#fff', border: 'none', fontSize: 15, fontWeight: 600,
          cursor: 'pointer',
        }}>
          Sign In
        </button>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '18px 0 14px' }}>
          <div style={{ flex: 1, height: 1, background: dark ? '#334155' : '#E2E8F0' }} />
          <span style={{ fontSize: 12, color: dark ? '#475569' : '#94A3B8', whiteSpace: 'nowrap' }}>or continue with</span>
          <div style={{ flex: 1, height: 1, background: dark ? '#334155' : '#E2E8F0' }} />
        </div>

        {GOOGLE_CLIENT_ID ? (
          <GoogleSignInButton
            onSuccess={handleGoogleSuccess}
            onError={handleGoogleError}
            loading={googleLoading}
            dark={dark}
          />
        ) : (
          <button
            onClick={() => setError('Google Sign-In is not configured yet. Contact your administrator.')}
            style={{
              width: '100%', padding: '11px 16px', borderRadius: 10,
              background: dark ? '#1E293B' : '#fff',
              border: `1px solid ${dark ? '#334155' : '#D1D5DB'}`,
              color: dark ? '#F1F5F9' : '#374151',
              fontSize: 14, fontWeight: 600, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
            }}
          >
            <GoogleIcon />
            Sign in with Google
          </button>
        )}
        <div style={{ textAlign: 'center', marginTop: 16 }}>
          <button onClick={toggleTheme} style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: dark ? '#94A3B8' : '#64748B', fontSize: 12,
            display: 'flex', alignItems: 'center', gap: 4, margin: '0 auto',
          }}>
            {dark ? <Sun size={14} /> : <Moon size={14} />} {dark ? 'Light' : 'Dark'} Mode
          </button>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
